'use client';
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';

export type ThemeMode = 'light' | 'dark' | 'system';

export type AccentColor =
  | 'amber' |'crimson' |'emerald' |'sapphire' |'violet' |'rose' |'teal' |'gold';

export type BackgroundStyle =
  | 'default' |'warm' |'cool' |'neutral' |'deep' |'slate';

export interface ThemePreferences {
  mode: ThemeMode;
  accentColor: AccentColor;
  backgroundStyle: BackgroundStyle;
}

interface ThemeContextValue extends ThemePreferences {
  resolvedTheme: 'light' | 'dark';
  setMode: (mode: ThemeMode) => void;
  setAccentColor: (color: AccentColor) => void;
  setBackgroundStyle: (style: BackgroundStyle) => void;
}

// ── Accent color HSL values (dark / light) ──────────────────────────────────
export const ACCENT_COLORS: Record<
  AccentColor,
  { label: string; dark: string; light: string; muted_dark: string; muted_light: string; fg_dark: string; fg_light: string; swatch: string }
> = {
  amber:    { label: 'Amber',    dark: '38 85% 52%',  light: '38 78% 42%',  muted_dark: '38 55% 28%', muted_light: '38 60% 62%', fg_dark: '24 20% 8%',  fg_light: '38 20% 98%', swatch: '#d97706' },
  crimson:  { label: 'Crimson',  dark: '0 80% 58%',   light: '0 72% 46%',   muted_dark: '0 50% 28%',  muted_light: '0 55% 62%',  fg_dark: '0 20% 8%',   fg_light: '0 20% 98%',  swatch: '#dc2626' },
  emerald:  { label: 'Emerald',  dark: '152 65% 48%', light: '152 58% 36%', muted_dark: '152 40% 24%',muted_light: '152 45% 56%',fg_dark: '152 20% 8%', fg_light: '152 20% 98%',swatch: '#059669' },
  sapphire: { label: 'Sapphire', dark: '217 85% 60%', light: '217 75% 46%', muted_dark: '217 55% 28%',muted_light: '217 60% 62%',fg_dark: '217 20% 8%', fg_light: '217 20% 98%',swatch: '#2563eb' },
  violet:   { label: 'Violet',   dark: '270 70% 62%', light: '270 62% 48%', muted_dark: '270 45% 28%',muted_light: '270 50% 62%',fg_dark: '270 20% 8%', fg_light: '270 20% 98%',swatch: '#7c3aed' },
  rose:     { label: 'Rose',     dark: '340 80% 60%', light: '340 70% 48%', muted_dark: '340 50% 28%',muted_light: '340 55% 62%',fg_dark: '340 20% 8%', fg_light: '340 20% 98%',swatch: '#e11d48' },
  teal:     { label: 'Teal',     dark: '180 65% 46%', light: '180 58% 34%', muted_dark: '180 40% 22%',muted_light: '180 45% 54%',fg_dark: '180 20% 8%', fg_light: '180 20% 98%',swatch: '#0d9488' },
  gold:     { label: 'Gold',     dark: '45 90% 55%',  light: '45 82% 42%',  muted_dark: '45 60% 26%', muted_light: '45 65% 60%', fg_dark: '45 20% 8%',  fg_light: '45 20% 98%',  swatch: '#ca8a04' },
};

// ── Background style HSL values (dark / light) ───────────────────────────────
export const BACKGROUND_STYLES: Record<
  BackgroundStyle,
  { label: string; dark: { bg: string; surface: string; raised: string; overlay: string }; light: { bg: string; surface: string; raised: string; overlay: string } }
> = {
  default: {
    label: 'Default',
    dark:  { bg: '24 12% 7%',  surface: '24 10% 10%', raised: '24 9% 13%',  overlay: '24 8% 17%'  },
    light: { bg: '38 28% 94%', surface: '38 22% 98%', raised: '38 18% 92%', overlay: '38 14% 87%' },
  },
  warm: {
    label: 'Warm',
    dark:  { bg: '20 18% 6%',  surface: '20 14% 9%',  raised: '20 12% 12%', overlay: '20 10% 16%' },
    light: { bg: '30 35% 93%', surface: '30 28% 97%', raised: '30 22% 90%', overlay: '30 18% 85%' },
  },
  cool: {
    label: 'Cool',
    dark:  { bg: '220 18% 7%', surface: '220 14% 10%',raised: '220 12% 13%',overlay: '220 10% 17%'},
    light: { bg: '210 30% 94%',surface: '210 24% 98%',raised: '210 18% 91%',overlay: '210 14% 86%'},
  },
  neutral: {
    label: 'Neutral',
    dark:  { bg: '0 0% 7%',    surface: '0 0% 10%',   raised: '0 0% 13%',   overlay: '0 0% 17%'   },
    light: { bg: '0 0% 94%',   surface: '0 0% 98%',   raised: '0 0% 91%',   overlay: '0 0% 86%'   },
  },
  deep: {
    label: 'Deep',
    dark:  { bg: '240 20% 5%', surface: '240 16% 8%', raised: '240 14% 11%',overlay: '240 12% 15%'},
    light: { bg: '240 20% 94%',surface: '240 16% 98%',raised: '240 12% 91%',overlay: '240 10% 86%'},
  },
  slate: {
    label: 'Slate',
    dark:  { bg: '215 25% 7%', surface: '215 20% 10%',raised: '215 18% 13%',overlay: '215 15% 17%'},
    light: { bg: '215 20% 93%',surface: '215 16% 97%',raised: '215 12% 90%',overlay: '215 10% 85%'},
  },
};

const DEFAULT_PREFS: ThemePreferences = {
  mode: 'dark',
  accentColor: 'amber',
  backgroundStyle: 'default',
};

const ThemeContext = createContext<ThemeContextValue>({
  ...DEFAULT_PREFS,
  resolvedTheme: 'dark',
  setMode: () => {},
  setAccentColor: () => {},
  setBackgroundStyle: () => {},
});

export function useTheme() {
  return useContext(ThemeContext);
}

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyThemeToDOM(prefs: ThemePreferences, resolved: 'light' | 'dark') {
  const root = document.documentElement;

  // Mode classes
  if (resolved === 'dark') {
    root.classList.remove('light-theme');
    root.classList.add('dark-theme');
  } else {
    root.classList.remove('dark-theme');
    root.classList.add('light-theme');
  }

  // Accent color CSS vars
  const accent = ACCENT_COLORS[prefs.accentColor];
  root.style.setProperty('--primary', resolved === 'dark' ? accent.dark : accent.light);
  root.style.setProperty('--primary-foreground', resolved === 'dark' ? accent.fg_dark : accent.fg_light);
  root.style.setProperty('--primary-muted', resolved === 'dark' ? accent.muted_dark : accent.muted_light);

  // Background CSS vars
  const bg = BACKGROUND_STYLES[prefs.backgroundStyle];
  const bgVars = resolved === 'dark' ? bg.dark : bg.light;
  root.style.setProperty('--background', bgVars.bg);
  root.style.setProperty('--surface', bgVars.surface);
  root.style.setProperty('--surface-raised', bgVars.raised);
  root.style.setProperty('--surface-overlay', bgVars.overlay);
}

function loadLocalPrefs(): ThemePreferences {
  try {
    const raw = localStorage.getItem('titan-theme-prefs');
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<ThemePreferences>;
      return {
        mode: (['light', 'dark', 'system'] as ThemeMode[]).includes(parsed.mode as ThemeMode)
          ? (parsed.mode as ThemeMode)
          : DEFAULT_PREFS.mode,
        accentColor: Object.keys(ACCENT_COLORS).includes(parsed.accentColor as string)
          ? (parsed.accentColor as AccentColor)
          : DEFAULT_PREFS.accentColor,
        backgroundStyle: Object.keys(BACKGROUND_STYLES).includes(parsed.backgroundStyle as string)
          ? (parsed.backgroundStyle as BackgroundStyle)
          : DEFAULT_PREFS.backgroundStyle,
      };
    }
  } catch {}
  return DEFAULT_PREFS;
}

function saveLocalPrefs(prefs: ThemePreferences) {
  try {
    localStorage.setItem('titan-theme-prefs', JSON.stringify(prefs));
  } catch {}
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [prefs, setPrefs] = useState<ThemePreferences>(DEFAULT_PREFS);
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>('dark');
  const supabase = createClient();
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Resolve theme from mode
  const resolveTheme = useCallback((mode: ThemeMode): 'light' | 'dark' => {
    return mode === 'system' ? getSystemTheme() : mode;
  }, []);

  // Apply + persist prefs
  const applyPrefs = useCallback(
    (newPrefs: ThemePreferences) => {
      const resolved = resolveTheme(newPrefs.mode);
      setPrefs(newPrefs);
      setResolvedTheme(resolved);
      saveLocalPrefs(newPrefs);
      applyThemeToDOM(newPrefs, resolved);

      // Debounced Supabase save
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(async () => {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) return;
          await supabase.from('user_theme_preferences').upsert(
            {
              user_id: user.id,
              theme_mode: newPrefs.mode,
              accent_color: newPrefs.accentColor,
              background_style: newPrefs.backgroundStyle,
              updated_at: new Date().toISOString(),
            },
            { onConflict: 'user_id' }
          );
        } catch {}
      }, 800);
    },
    [resolveTheme, supabase]
  );

  // Initialize: load from localStorage first (instant), then Supabase (async)
  useEffect(() => {
    const local = loadLocalPrefs();
    const resolved = resolveTheme(local.mode);
    setPrefs(local);
    setResolvedTheme(resolved);
    applyThemeToDOM(local, resolved);

    // Async load from Supabase
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data } = await supabase
          .from('user_theme_preferences')
          .select('theme_mode, accent_color, background_style')
          .eq('user_id', user.id)
          .single();
        if (data) {
          const remote: ThemePreferences = {
            mode: data.theme_mode as ThemeMode,
            accentColor: data.accent_color as AccentColor,
            backgroundStyle: data.background_style as BackgroundStyle,
          };
          const remoteResolved = resolveTheme(remote.mode);
          setPrefs(remote);
          setResolvedTheme(remoteResolved);
          saveLocalPrefs(remote);
          applyThemeToDOM(remote, remoteResolved);
        }
      } catch {}
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // System theme listener
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      if (prefs.mode === 'system') {
        const resolved = getSystemTheme();
        setResolvedTheme(resolved);
        applyThemeToDOM(prefs, resolved);
      }
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [prefs]);

  const setMode = useCallback(
    (mode: ThemeMode) => applyPrefs({ ...prefs, mode }),
    [prefs, applyPrefs]
  );

  const setAccentColor = useCallback(
    (accentColor: AccentColor) => applyPrefs({ ...prefs, accentColor }),
    [prefs, applyPrefs]
  );

  const setBackgroundStyle = useCallback(
    (backgroundStyle: BackgroundStyle) => applyPrefs({ ...prefs, backgroundStyle }),
    [prefs, applyPrefs]
  );

  return (
    <ThemeContext.Provider
      value={{ ...prefs, resolvedTheme, setMode, setAccentColor, setBackgroundStyle }}
    >
      {children}
    </ThemeContext.Provider>
  );
}
